import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';

import TopNav from './components/builder/TopNav';
import LeftSidebar from './components/builder/LeftSidebar';
import ElementsPanel from './components/builder/ElementsPanel';
import PagesPanel from './components/builder/PagesPanel';
import Canvas from './components/builder/Canvas';

import EcommerceDashboard from './components/dashboard/EcommerceDashboard';
import SitesFunnelsDashboard from './components/dashboard/SitesFunnelsDashboard';
import SuperAdminDashboard from './components/dashboard/SuperAdminDashboard';
import LandingPage from './components/landing/LandingPage';
import AuthModal from './components/auth/AuthModal';
import { CartProvider } from './context/CartContext';
import CartSidebar from './components/store/CartSidebar';
import CheckoutModal from './components/store/CheckoutModal';

import { DEFAULT_FOOTER } from './components/builder/GlobalFooter';
import { usePagesPersistence } from './hooks/usePagesPersistence';
import { useLayoutSave } from './hooks/useLayoutSave';
import supabase, {
  withRetry, checkConnectionHealth, getConnectionStatus,
  getCached, setCached, lsGet, lsSet
} from './supabase/supabase';
import SafeIcon from './components/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiGrid, FiAlertCircle, FiCheckCircle, FiExternalLink, FiWifi, FiWifiOff, FiRefreshCw } = FiIcons;

import LivePreviewOverlay from './components/builder/LivePreviewOverlay';
import { deployStaticSite, createNetlifySite, setNetlifyPrimaryDomain } from './utils/netlify';
import './index.css';

// ─── Defaults ─────────────────────────────────────────────────────────────────
const FRESH_MENU_STYLES = { backgroundColor: '#ffffff', textColor: '#000000', hoverColor: '#a3cc00', buttonBgColor: '#000000', buttonTextColor: '#ffffff' };
const FRESH_MENU_ITEMS  = [{ id: 1, label: 'Home', link: '/', linkType: 'url' }];
const FRESH_LOGO        = { type: 'text', content: 'MY BRAND', url: '' };
const FRESH_FOOTER      = { ...DEFAULT_FOOTER, backgroundColor: '#000000', accentColor: '#ccff00' };
const FRESH_SOCIAL      = { tiktok: '', instagram: '', facebook: '' };

// ─── Cache key helpers ────────────────────────────────────────────────────────
const siteMetaCacheKey  = (id) => `site_meta_${id}`;
const layoutCacheKey    = (id) => `layout_${id}`;
const pagesCacheKey     = (id) => `pages_${id}`;

function resetAppState(setters) {
  setters.setMenuStyles(FRESH_MENU_STYLES);
  setters.setMenuItems(FRESH_MENU_ITEMS);
  setters.setLogoData(FRESH_LOGO);
  setters.setFooterData(FRESH_FOOTER);
  setters.setSocialLinks(FRESH_SOCIAL);
  setters.setPages([]);
  setters.setPageContents({});
  setters.setActivePageId(null);
  setters.setActiveWebsiteId(null);
  setters.setActiveWebsiteName('');
  setters.setIsPublished(false);
  setters.setNetlifySiteId(null);
  setters.setNetlifyUrl(null);
  setters.setNetlifySubdomain(null);
  if (setters.setPublishError) setters.setPublishError(null);
}

// ─── Startup Progress Bar ─────────────────────────────────────────────────────
function StartupProgressBar({ progress }) {
  return (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
      <div className="w-full max-w-md px-6">
        <div className="flex items-center space-x-3 mb-8 justify-center">
          <div className="w-12 h-12 bg-[#c8f542] rounded-xl flex items-center justify-center">
            <span className="text-black font-black text-xl">S</span>
          </div>
          <h2 className="text-2xl font-black text-white">SideHustlr</h2>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between text-sm font-bold text-gray-400">
            <span>Loading</span><span>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[#c8f542] to-[#a3cc00]"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Connection Status Indicator ──────────────────────────────────────────────
function ConnectionStatusIndicator({ isConnected }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isConnected) {
      setShow(true);
    } else {
      const timer = setTimeout(() => setShow(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isConnected]);

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`fixed top-4 right-4 z-[999999] px-4 py-3 rounded-xl shadow-lg flex items-center space-x-3 ${
          isConnected ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}
      >
        <SafeIcon icon={isConnected ? FiWifi : FiWifiOff} className="w-5 h-5" />
        <div>
          <p className="font-bold text-sm">{isConnected ? 'Connection Restored' : 'Connection Lost'}</p>
          <p className="text-xs opacity-90">{isConnected ? 'Syncing your changes...' : 'Attempting to reconnect...'}</p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Background Sync Indicator (subtle) ───────────────────────────────────────
function SyncIndicator({ syncing }) {
  return (
    <AnimatePresence>
      {syncing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed bottom-24 right-5 z-[9999] flex items-center space-x-2 bg-black/70 text-white text-[10px] font-bold px-3 py-1.5 rounded-full backdrop-blur-sm"
        >
          <SafeIcon icon={FiRefreshCw} className="w-3 h-3 animate-spin" />
          <span>Syncing...</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Parse layout helper ──────────────────────────────────────────────────────
function parseLayout(data) {
  if (!data) return null;
  const legacy = data.layout_data || {};
  const activeUrl = data.custom_domain
    ? `https://${data.custom_domain}`
    : data.netlify_subdomain
      ? `https://${data.netlify_subdomain}`
      : data.netlify_url || null;
  return {
    logo_data:        data.logo_data        || legacy.logo_data        || null,
    menu_items:       data.menu_items       || legacy.menu_items       || null,
    menu_styles:      data.menu_styles      || legacy.menu_styles      || null,
    social_links:     data.social_links     || legacy.social_links     || null,
    footer_data:      data.footer_data      || legacy.footer_data      || null,
    netlify_site_id:  data.netlify_site_id  || null,
    netlify_subdomain:data.netlify_subdomain|| null,
    netlify_url:      activeUrl,
    is_published:     data.is_published     || false,
  };
}

// ─── Apply parsed layout to state setters ────────────────────────────────────
function applyLayout(layout, setters) {
  if (!layout) return;
  if (layout.logo_data)        setters.setLogoData(layout.logo_data);
  if (layout.menu_items?.length) setters.setMenuItems(layout.menu_items);
  if (layout.menu_styles)      setters.setMenuStyles(layout.menu_styles);
  if (layout.social_links)     setters.setSocialLinks(layout.social_links);
  if (layout.footer_data)      setters.setFooterData(layout.footer_data);
  setters.setIsPublished(layout.is_published || false);
  setters.setNetlifySiteId(layout.netlify_site_id || null);
  setters.setNetlifySubdomain(layout.netlify_subdomain || null);
  setters.setNetlifyUrl(layout.netlify_url || null);
}

// ─── Apply loaded pages to state setters ─────────────────────────────────────
function applyPages(loadedPages, setters, projectId, savePage) {
  if (!loadedPages || loadedPages.length === 0) return false;
  const sorted = [...loadedPages].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  setters.setPages(sorted.map(p => ({ id: p.id, name: p.name, slug: p.slug, sort_order: p.sort_order })));
  const contentsMap = {};
  sorted.forEach(p => { contentsMap[p.id] = p.content || {}; });
  setters.setPageContents(contentsMap);
  setters.setActivePageId(sorted[0].id);
  return true;
}

// ─── Main App ────────────────────────────────────────────────────────────────
function App() {
  const [isPreviewMode] = useState(() => {
    try { return new URL(window.location.href).searchParams.get('preview') === 'true'; } catch { return false; }
  });
  const [isLiveMode] = useState(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('preview') === 'true') return false;
      return url.pathname.replace(/^\/|\/$/g, '').length > 0;
    } catch { return false; }
  });

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [appStage, setAppStage] = useState('landing');
  const [view, setView] = useState('builder');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isConnected, setIsConnected] = useState(true);
  const [isBgSyncing, setIsBgSyncing] = useState(false);

  const [activeWebsiteId, setActiveWebsiteId] = useState(null);
  const [activeWebsiteName, setActiveWebsiteName] = useState('');

  const [isPublished, setIsPublished] = useState(false);
  const [netlifySiteId, setNetlifySiteId] = useState(null);
  const [netlifySubdomain, setNetlifySubdomain] = useState(null);
  const [netlifyUrl, setNetlifyUrl] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [publishError, setPublishError] = useState(null);

  const [showSitesPopup, setShowSitesPopup] = useState(false);
  const [showStoreDashboardPopup, setShowStoreDashboardPopup] = useState(false);
  const [showSuperAdminDashboard, setShowSuperAdminDashboard] = useState(false);

  const [elementsPanelOpen, setElementsPanelOpen] = useState(false);
  const [pagesPanelOpen, setPagesPanelOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(280);

  const [menuStyles, setMenuStyles] = useState(FRESH_MENU_STYLES);
  const [socialLinks, setSocialLinks] = useState(FRESH_SOCIAL);
  const [menuItems, setMenuItems] = useState(FRESH_MENU_ITEMS);
  const [logoData, setLogoData] = useState(FRESH_LOGO);
  const [footerData, setFooterData] = useState(FRESH_FOOTER);

  const [pages, setPages] = useState([]);
  const [activePageId, setActivePageId] = useState(null);
  const [pageContents, setPageContents] = useState({});
  const [historyState, setHistoryState] = useState({ canUndo: false, canRedo: false });
  const [showPreview, setShowPreview] = useState(false);
  const [pendingWebsiteId, setPendingWebsiteId] = useState(null);

  const canvasRef = useRef(null);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const fallbackStateRef = useRef(null);
  const pagesRef = useRef(pages);
  const pageContentsRef = useRef(pageContents);
  const currentUserId = useRef(null);
  const bgSyncTimer = useRef(null);

  useEffect(() => { pagesRef.current = pages; }, [pages]);
  useEffect(() => { pageContentsRef.current = pageContents; }, [pageContents]);

  const activeWebsiteIdRef   = useRef(activeWebsiteId);
  const menuStylesRef        = useRef(menuStyles);
  const menuItemsRef         = useRef(menuItems);
  const logoDataRef          = useRef(logoData);
  const footerDataRef        = useRef(footerData);
  const socialLinksRef       = useRef(socialLinks);

  useEffect(() => { activeWebsiteIdRef.current = activeWebsiteId; }, [activeWebsiteId]);
  useEffect(() => { menuStylesRef.current = menuStyles; }, [menuStyles]);
  useEffect(() => { menuItemsRef.current = menuItems; }, [menuItems]);
  useEffect(() => { logoDataRef.current = logoData; }, [logoData]);
  useEffect(() => { footerDataRef.current = footerData; }, [footerData]);
  useEffect(() => { socialLinksRef.current = socialLinks; }, [socialLinks]);

  const { loadPages, savePage, savePageContent, savePageContentImmediate, deletePage, updatePageMeta } = usePagesPersistence();
  const { loadLayout, saveLayout } = useLayoutSave();

  // ── Connection health monitoring ──────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      const status = getConnectionStatus();
      setIsConnected(status.isHealthy);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // ── Check if user is super admin ──────────────────────────────────────────
  const checkSuperAdmin = useCallback(async (userObj) => {
    if (!userObj) {
      setIsSuperAdmin(false);
      return;
    }

    if (userObj.email === 'theo@phonefactory.co.za') {
      setIsSuperAdmin(true);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('super_admin_users')
        .select('id')
        .eq('id', userObj.id)
        .single();
      
      setIsSuperAdmin(!!data && !error);
    } catch (err) {
      setIsSuperAdmin(false);
    }
  }, []);

  // ── Auth init ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isPreviewMode || isLiveMode) { setAuthChecked(true); return; }

    const initAuth = async () => {
      try {
        setLoadingProgress(10);
        
        // Fast fail timeout for initial auth check (max 5 seconds) to prevent 10% hang
        const sessionResult = await withRetry(() => supabase.auth.getSession(), 1, 1000, 5000)
          .catch(() => ({ data: { session: null }, error: new Error('Auth timeout') }));
          
        setLoadingProgress(40);
        
        if (sessionResult?.data?.session?.user) {
          const authUser = sessionResult.data.session.user;
          currentUserId.current = authUser.id;
          setUser(authUser);
          
          // These are now protected by internal withRetry timeouts
          await Promise.allSettled([
            loadUserProfile(authUser.id),
            checkSuperAdmin(authUser)
          ]);
          
          setLoadingProgress(100);
          setAppStage('builder');
        } else {
          setLoadingProgress(100);
          setAppStage('landing');
        }
      } catch (err) {
        console.error('Auth error:', err);
        setLoadingProgress(100);
        setAppStage('landing');
      } finally {
        setTimeout(() => setAuthChecked(true), 300);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        if (currentUserId.current !== session.user.id) {
          currentUserId.current = session.user.id;
          resetAppState({ setMenuStyles, setMenuItems, setLogoData, setFooterData, setSocialLinks, setPages, setPageContents, setActivePageId, setActiveWebsiteId, setActiveWebsiteName, setIsPublished, setNetlifySiteId, setNetlifyUrl, setNetlifySubdomain, setPublishError });
        }
        setUser(session.user);
        await loadUserProfile(session.user.id);
        await checkSuperAdmin(session.user);
        setAppStage('builder');
      } else if (event === 'SIGNED_OUT') {
        currentUserId.current = null;
        setUser(null); setProfile(null); setIsSuperAdmin(false);
        setAppStage('landing');
        resetAppState({ setMenuStyles, setMenuItems, setLogoData, setFooterData, setSocialLinks, setPages, setPageContents, setActivePageId, setActiveWebsiteId, setActiveWebsiteName, setIsPublished, setNetlifySiteId, setNetlifyUrl, setNetlifySubdomain, setPublishError });
        setShowSitesPopup(false); setShowStoreDashboardPopup(false); setShowSuperAdminDashboard(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [isPreviewMode, isLiveMode, checkSuperAdmin]);

  const loadUserProfile = async (userId) => {
    try {
      const result = await withRetry(() => supabase.from('user_profiles').select('*').eq('id', userId).single(), 2, 1000, 5000);
      if (result.error && result.error.code !== 'PGRST116') throw result.error;
      if (result.data) setProfile(result.data);
    } catch (err) { console.error('Profile load error:', err); }
  };

  const handleAuthSuccess = async (authUser) => {
    currentUserId.current = authUser.id;
    resetAppState({ setMenuStyles, setMenuItems, setLogoData, setFooterData, setSocialLinks, setPages, setPageContents, setActivePageId, setActiveWebsiteId, setActiveWebsiteName, setIsPublished, setNetlifySiteId, setNetlifyUrl, setNetlifySubdomain, setPublishError });
    setUser(authUser);
    try {
      const { data: existingProfile } = await withRetry(() => supabase.from('user_profiles').select('*').eq('id', authUser.id).single(), 2, 1000);
      if (existingProfile) {
        setProfile(existingProfile);
      } else {
        const newProfile = { id: authUser.id, email: authUser.email, full_name: '', updated_at: new Date().toISOString() };
        await withRetry(() => supabase.from('user_profiles').insert(newProfile), 2, 1000);
        setProfile(newProfile);
      }
      await checkSuperAdmin(authUser);
    } catch (e) { console.warn('Profile creation warning:', e); }
    setShowAuthModal(false);
    setAppStage('builder');
  };

  const handleLogout = async () => { await supabase.auth.signOut(); };

  useEffect(() => {
    fallbackStateRef.current = {
      droppedElements: activePageId && pageContents[activePageId] ? (pageContents[activePageId].droppedElements || []) : [],
      headerPadding: activePageId && pageContents[activePageId] ? (pageContents[activePageId].headerPadding || { top: 32, bottom: 32 }) : { top: 32, bottom: 32 },
      canvasPadding: activePageId && pageContents[activePageId] ? (pageContents[activePageId].canvasPadding || { top: 64, bottom: 64 }) : { top: 64, bottom: 64 },
      logoData, menuItems, currentSocialLinks: socialLinks, menuStyles, footerData,
    };
  }, [activePageId, pageContents, logoData, menuItems, socialLinks, menuStyles, footerData]);

  const persistLayout = useCallback(async (overrides = {}) => {
    const websiteId = activeWebsiteIdRef.current;
    if (!websiteId) return;
    await saveLayout(websiteId, {
      logoData:    overrides.logoData    ?? logoDataRef.current,
      menuItems:   overrides.menuItems   ?? menuItemsRef.current,
      menuStyles:  overrides.menuStyles  ?? menuStylesRef.current,
      socialLinks: overrides.socialLinks ?? socialLinksRef.current,
      footerData:  overrides.footerData  ?? footerDataRef.current,
    });
  }, [saveLayout]);

  // ── Find the most recent website ─────────────────────────────────────────
  useEffect(() => {
    if (isPreviewMode || isLiveMode || !user) return;
    const checkInitialSite = async () => {
      try {
        const result = await withRetry(
          () => supabase.from('websites_1772750000000').select('id,name').eq('user_id', user.id).order('updated_at', { ascending: false }).limit(1),
          3, 1500
        );
        if (result.error) throw result.error;
        if (result.data && result.data.length > 0) {
          setPendingWebsiteId({ id: result.data[0].id, name: result.data[0].name });
        } else {
          setShowSitesPopup(true);
        }
      } catch (e) {
        console.error('Website check error:', e);
        setShowSitesPopup(true);
      }
    };
    checkInitialSite();
  }, [isPreviewMode, isLiveMode, user]);

  // ── INSTANT CACHE LOAD — then background revalidate ───────────────────────
  useEffect(() => {
    if (!pendingWebsiteId || isPreviewMode || isLiveMode) return;
    const { id, name } = pendingWebsiteId;

    // Reset UI state immediately
    setActiveWebsiteId(id);
    setActiveWebsiteName(name || '');
    setIsPublished(false);
    setNetlifySiteId(null);
    setNetlifyUrl(null);
    setNetlifySubdomain(null);
    setPublishError(null);

    // Reset layout to defaults first
    setLogoData({ ...FRESH_LOGO });
    setMenuItems(FRESH_MENU_ITEMS);
    setMenuStyles(FRESH_MENU_STYLES);
    setFooterData({ ...FRESH_FOOTER });
    setSocialLinks({ ...FRESH_SOCIAL });

    let didApplyCachedPages = false;

    // ── STEP 1: Try to render canvas instantly from cache ──────────────────
    const cachedLayout = getCached(layoutCacheKey(id)) || lsGet(layoutCacheKey(id));
    if (cachedLayout) {
      const parsed = parseLayout(cachedLayout);
      applyLayout(parsed, { setLogoData, setMenuItems, setMenuStyles, setSocialLinks, setFooterData, setIsPublished, setNetlifySiteId, setNetlifySubdomain, setNetlifyUrl });
    }

    const cachedPages = getCached(pagesCacheKey(id)) || lsGet(pagesCacheKey(id));
    if (cachedPages && cachedPages.length > 0) {
      const applied = applyPages(cachedPages, { setPages, setPageContents, setActivePageId }, id, savePage);
      didApplyCachedPages = applied;
    }

    // ── STEP 2: Background revalidate from Supabase ────────────────────────
    const bgRevalidate = async () => {
      setIsBgSyncing(true);
      try {
        // Fetch layout + pages in parallel
        const [layoutResult, pagesResult, nameResult] = await Promise.allSettled([
          supabase.from('websites_1772750000000')
            .select('logo_data,menu_items,menu_styles,social_links,footer_data,layout_data,netlify_site_id,netlify_url,netlify_subdomain,custom_domain,is_published')
            .eq('id', id).single(),
          supabase.from('site_pages')
            .select('id,name,slug,sort_order,content,project_id')
            .eq('project_id', id).order('sort_order', { ascending: true }),
          !name
            ? supabase.from('websites_1772750000000').select('name').eq('id', id).single()
            : Promise.resolve({ value: null }),
        ]);

        // Apply fresh layout
        if (layoutResult.status === 'fulfilled' && layoutResult.value?.data) {
          const freshLayout = layoutResult.value.data;
          setCached(layoutCacheKey(id), freshLayout);
          lsSet(layoutCacheKey(id), freshLayout);
          const parsed = parseLayout(freshLayout);
          applyLayout(parsed, { setLogoData, setMenuItems, setMenuStyles, setSocialLinks, setFooterData, setIsPublished, setNetlifySiteId, setNetlifySubdomain, setNetlifyUrl });
        }

        // Apply fresh pages
        if (pagesResult.status === 'fulfilled' && pagesResult.value?.data) {
          const freshPages = pagesResult.value.data;
          setCached(pagesCacheKey(id), freshPages);
          lsSet(pagesCacheKey(id), freshPages);

          if (freshPages.length > 0) {
            // Always apply fresh pages — they're the source of truth
            applyPages(freshPages, { setPages, setPageContents, setActivePageId }, id, savePage);
          } else if (!didApplyCachedPages) {
            // Brand-new site: create default home page
            const defaultHomeId = `page-home-${Date.now()}`;
            const defaultHome = { id: defaultHomeId, name: 'Home', slug: '/', sort_order: 0 };
            setPages([defaultHome]);
            setPageContents({ [defaultHomeId]: {} });
            setActivePageId(defaultHomeId);
            await savePage(id, { ...defaultHome, content: {} });
          }
        } else if (!didApplyCachedPages) {
          // Fetch failed and no cache: create default
          const defaultHomeId = `page-home-${Date.now()}`;
          const defaultHome = { id: defaultHomeId, name: 'Home', slug: '/', sort_order: 0 };
          setPages([defaultHome]);
          setPageContents({ [defaultHomeId]: {} });
          setActivePageId(defaultHomeId);
          await savePage(id, { ...defaultHome, content: {} });
        }

        // Apply site name if fetched
        if (nameResult.status === 'fulfilled' && nameResult.value?.value?.data?.name) {
          setActiveWebsiteName(nameResult.value.value.data.name);
        }
      } catch (err) {
        console.error('Background revalidate error:', err);
      } finally {
        setIsBgSyncing(false);
      }
    };

    // If cache hit → render canvas immediately, revalidate in background after tiny delay
    // If no cache  → show canvas skeleton + start fetch immediately
    if (didApplyCachedPages) {
      // Canvas is already rendered — do background sync after 200ms
      bgSyncTimer.current = setTimeout(bgRevalidate, 200);
    } else {
      // No cache — fetch right away (will render canvas once pages arrive)
      bgRevalidate();
    }

    setPendingWebsiteId(null);

    return () => { if (bgSyncTimer.current) clearTimeout(bgSyncTimer.current); };
  }, [pendingWebsiteId, isPreviewMode, isLiveMode]);

  const handleOpenPreview = useCallback(() => setShowPreview(true), []);
  const handleClosePreview = useCallback(() => setShowPreview(false), []);

  const handleOpenWebsite = useCallback((id, name) => {
    setShowSitesPopup(false);
    setShowStoreDashboardPopup(false);
    setShowSuperAdminDashboard(false);
    setView('builder');
    setPendingWebsiteId({ id, name: name || '' });
  }, []);

  const toggleElementsPanel = () => { setElementsPanelOpen(prev => !prev); if (!elementsPanelOpen) setPagesPanelOpen(false); };
  const togglePagesPanel    = () => { setPagesPanelOpen(prev => !prev); if (!pagesPanelOpen) setElementsPanelOpen(false); };

  const onMouseDown = useCallback((e) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = panelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (e) => {
      if (!isResizing.current) return;
      setPanelWidth(Math.min(600, Math.max(220, startWidth.current + (e.clientX - startX.current))));
    };
    const onMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [panelWidth]);

  const handleCanvasStateChange = useCallback((pageId, state) => {
    setPageContents(prev => ({ ...prev, [pageId]: state }));
    savePageContent(activeWebsiteId, pageId, state);
    setIsPublished(false);
    setPublishError(null);
  }, [activeWebsiteId, savePageContent]);

  const handleSaveComplete = useCallback(async (pageId, state) => {
    if (!activeWebsiteId) return { success: false, error: 'No active project selected.' };
    try {
      const layoutResult = await saveLayout(activeWebsiteId, { logoData, menuItems, menuStyles, socialLinks, footerData });
      if (layoutResult && layoutResult.success === false) return layoutResult;
      if (pageId && state) {
        const pageResult = await savePageContentImmediate(activeWebsiteId, pageId, state);
        if (pageResult && pageResult.success === false) return pageResult;
      }
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message || 'An unexpected error occurred while saving.' };
    }
  }, [activeWebsiteId, logoData, menuItems, menuStyles, socialLinks, footerData, saveLayout, savePageContentImmediate]);

  const handleLogoDataChange = useCallback((newLogoData) => {
    setLogoData(newLogoData); setIsPublished(false); setPublishError(null);
    if (activeWebsiteIdRef.current) persistLayout({ logoData: newLogoData });
  }, [persistLayout]);

  const handleMenuStylesChange = useCallback((styles) => {
    setMenuStyles(styles); setIsPublished(false); setPublishError(null);
    if (activeWebsiteIdRef.current) persistLayout({ menuStyles: styles });
  }, [persistLayout]);

  const handleMenuItemsChange = useCallback((items) => {
    setMenuItems(items); setIsPublished(false); setPublishError(null);
    if (activeWebsiteIdRef.current) persistLayout({ menuItems: items });
  }, [persistLayout]);

  const handleFooterDataChange = useCallback((data) => {
    setFooterData(data); setIsPublished(false); setPublishError(null);
    if (activeWebsiteIdRef.current) persistLayout({ footerData: data });
  }, [persistLayout]);

  const handleSelectPage = (pageId) => {
    if (pageId === activePageId) return;
    if (canvasRef.current?.getState) {
      const currentState = canvasRef.current.getState();
      handleCanvasStateChange(activePageId, currentState);
    }
    setActivePageId(pageId);
  };

  const handleAddPage = async (name = 'New Page') => {
    if (!activeWebsiteId) return;
    const id = `page-${Date.now()}`;
    const slug = '/' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const newPage = { id, name, slug, sort_order: pages.length };
    setPages(prev => [...prev, newPage]);
    setPageContents(prev => ({ ...prev, [id]: {} }));
    setActivePageId(id);
    await savePage(activeWebsiteId, { ...newPage, content: {} });
  };

  const handleDuplicatePage = async (id) => {
    if (!activeWebsiteId) return;
    const source = pages.find(p => p.id === id);
    if (!source) return;
    if (canvasRef.current?.getState) {
      const currentState = canvasRef.current.getState();
      handleCanvasStateChange(activePageId, currentState);
    }
    const newId = `page-${Date.now()}`;
    const newPage = { id: newId, name: `${source.name} (Copy)`, slug: source.slug + '-copy', sort_order: pages.length };
    const contentCopy = JSON.parse(JSON.stringify(pageContents[id] || {}));
    setPages(prev => [...prev, newPage]);
    setPageContents(prev => ({ ...prev, [newId]: contentCopy }));
    setActivePageId(newId);
    await savePage(activeWebsiteId, { ...newPage, content: contentCopy });
  };

  const handleDeletePage = async (id) => {
    if (!activeWebsiteId || pages.length <= 1) return;
    const remain = pages.filter(p => p.id !== id);
    setPages(remain);
    setPageContents(prev => { const next = { ...prev }; delete next[id]; return next; });
    if (activePageId === id) setActivePageId(remain[0].id);
    await deletePage(activeWebsiteId, id);
  };

  const handleRenamePage = async (id, name) => {
    if (!activeWebsiteId) return;
    const slug = '/' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setPages(prev => prev.map(p => p.id === id ? { ...p, name, slug } : p));
    await updatePageMeta(activeWebsiteId, id, { name, slug });
  };

  const handleReorderPages = async (newOrder) => {
    if (!activeWebsiteId) return;
    const updated = newOrder.map((p, idx) => ({ ...p, sort_order: idx }));
    setPages(updated);
    await Promise.all(updated.map(p => updatePageMeta(activeWebsiteId, p.id, { sort_order: p.sort_order })));
  };

  const handleNavigateToPage = (slug) => {
    const targetPage = pages.find(p => p.slug === slug);
    if (targetPage && targetPage.id !== activePageId) handleSelectPage(targetPage.id);
  };

  const handlePublish = async () => {
    if (!activeWebsiteId || isPublishing) return;
    setIsPublishing(true); setPublishError(null);

    let currentNetlifySiteId = netlifySiteId;
    let currentNetlifySubdomain = netlifySubdomain;
    const isFirstPublish = !currentNetlifySiteId;

    try {
      if (isFirstPublish) {
        const netlifyData = await createNetlifySite(activeWebsiteName || 'my-site');
        currentNetlifySiteId = netlifyData.netlify_site_id;
        currentNetlifySubdomain = netlifyData.subdomain;
        setNetlifySiteId(currentNetlifySiteId);
        setNetlifySubdomain(currentNetlifySubdomain);
        setNetlifyUrl(`https://${currentNetlifySubdomain}`);
        try {
          await withRetry(() => supabase.from('websites_1772750000000').update({
            netlify_site_id: currentNetlifySiteId,
            netlify_site_name: netlifyData.netlify_site_name,
            netlify_subdomain: currentNetlifySubdomain,
            netlify_url: `https://${currentNetlifySubdomain}`,
            netlify_slug: netlifyData.netlify_slug,
          }).eq('id', activeWebsiteId), 2, 1500);
        } catch (dbErr) { console.warn('Could not save netlify fields to DB:', dbErr.message); }
        if (currentNetlifySubdomain) {
          try { await setNetlifyPrimaryDomain(currentNetlifySiteId, currentNetlifySubdomain); }
          catch (domainErr) { console.warn('Could not set primary domain (non-fatal):', domainErr.message); }
        }
      }

      const sitePages = pages.map(p => ({
        slug: p.slug, name: p.name,
        content: pageContents[p.id] || { droppedElements: [], headerPadding: { top: 32, bottom: 32 }, canvasPadding: { top: 64, bottom: 64 } },
      }));
      const globalData = { logoData, menuItems, menuStyles, footerData };
      await deployStaticSite(currentNetlifySiteId, sitePages, globalData, activeWebsiteId);

      try {
        await withRetry(() => supabase.from('websites_1772750000000').update({ is_published: true, updated_at: new Date().toISOString() }).eq('id', activeWebsiteId), 2, 1500);
      } catch (dbErr) { console.warn('Non-fatal: Could not update is_published:', dbErr.message); }

      if (currentNetlifySubdomain) setNetlifyUrl(`https://${currentNetlifySubdomain}`);
      setIsPublished(true);
      setShowLiveModal(true);
      confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 }, colors: ['#c8f542', '#2dd4bf', '#ffffff'], zIndex: 9999999 });

    } catch (err) {
      console.error('Publish failed:', err);
      let errorMessage = err.message || 'An unexpected error occurred while publishing your site.';
      if (errorMessage.includes('Netlify API token not configured')) errorMessage = 'Missing Netlify API Token. Please add VITE_NETLIFY_TOKEN to your environment variables.';
      else if (errorMessage.includes('Failed to fetch') || errorMessage.includes('Load failed')) errorMessage = 'Network error. Please check your internet connection or disable ad-blockers.';
      else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) errorMessage = 'Invalid Netlify API Token. Please verify your VITE_NETLIFY_TOKEN is correct.';
      else if (errorMessage.includes('404')) errorMessage = 'Site not found on Netlify. It may have been deleted. Try creating a new project.';
      setPublishError(errorMessage);
    } finally {
      setIsPublishing(false);
    }
  };

  const activePage   = pages.find(p => p.id === activePageId) || pages[0];
  const sidePanelOpen = elementsPanelOpen || pagesPanelOpen;

  if (!authChecked) return <StartupProgressBar progress={loadingProgress} />;

  if (appStage === 'landing') {
    return (
      <CartProvider projectId={null}>
        <LandingPage onEnterApp={() => setShowAuthModal(true)} />
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} onSuccess={handleAuthSuccess} />
      </CartProvider>
    );
  }

  return (
    <CartProvider projectId={activeWebsiteId}>
      <div className="h-screen w-full bg-white flex flex-col overflow-hidden font-sans">
        <ConnectionStatusIndicator isConnected={isConnected} />
        <SyncIndicator syncing={isBgSyncing} />

        <AnimatePresence mode="wait">
          {view === 'builder' && !showSuperAdminDashboard && (
            <motion.div key="builder" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col h-full w-full">
              <TopNav
                view={view}
                activeWebsiteName={activeWebsiteName}
                onOpenSites={() => setShowSitesPopup(true)}
                onOpenBuilder={() => setView('builder')}
                onOpenStore={() => setShowStoreDashboardPopup(true)}
                onOpenDashboard={isSuperAdmin ? () => { setShowSuperAdminDashboard(true); setView('dashboard'); } : null}
                menuStyles={menuStyles}
                onMenuStylesChange={handleMenuStylesChange}
                onUndo={() => canvasRef.current?.undo()}
                onRedo={() => canvasRef.current?.redo()}
                canUndo={historyState.canUndo}
                canRedo={historyState.canRedo}
                onPublish={handlePublish}
                isPublished={isPublished}
                isPublishing={isPublishing}
                publishError={publishError}
              />
              <div className="flex flex-1 overflow-hidden relative">
                <LeftSidebar
                  toggleElementsPanel={toggleElementsPanel}
                  elementsPanelOpen={elementsPanelOpen}
                  togglePagesPanel={togglePagesPanel}
                  pagesPanelOpen={pagesPanelOpen}
                  onPreviewClick={handleOpenPreview}
                  activeWebsiteName={activeWebsiteName}
                  activeWebsiteId={activeWebsiteId}
                  user={user}
                  profile={profile}
                  onLogout={handleLogout}
                  onProfileUpdate={updated => setProfile(updated)}
                  isSuperAdmin={isSuperAdmin}
                  onOpenDashboard={() => { setShowSuperAdminDashboard(true); setView('dashboard'); }}
                />

                {sidePanelOpen && (
                  <div className="flex shrink-0" style={{ width: panelWidth }}>
                    {elementsPanelOpen && <ElementsPanel isOpen={elementsPanelOpen} panelWidth={panelWidth} />}
                    {pagesPanelOpen && (
                      <PagesPanel
                        isOpen={pagesPanelOpen}
                        pages={pages}
                        activePageId={activePageId}
                        onAddPage={handleAddPage}
                        onSelectPage={handleSelectPage}
                        onDeletePage={handleDeletePage}
                        onRenamePage={handleRenamePage}
                        onDuplicatePage={handleDuplicatePage}
                        onReorderPages={handleReorderPages}
                        hasUnsavedChanges={false}
                      />
                    )}
                  </div>
                )}

                {sidePanelOpen && (
                  <div onMouseDown={onMouseDown} className="w-1.5 shrink-0 bg-gray-100 hover:bg-[#c8f542] active:bg-[#a3cc00] cursor-col-resize transition-colors duration-150 relative group z-20">
                    <div className="absolute inset-y-0 -left-1 -right-1" />
                  </div>
                )}

                {!activeWebsiteId ? (
                  <div className="flex-1 bg-[#f0f2f5] flex items-center justify-center flex-col">
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-sm mb-4">
                      <SafeIcon icon={FiGrid} className="w-8 h-8 text-gray-300" />
                    </div>
                    <p className="text-gray-400 font-bold uppercase tracking-widest text-sm mb-2">No Project Selected</p>
                    <p className="text-gray-300 text-xs mb-5">Create your first website to get started</p>
                    <button onClick={() => setShowSitesPopup(true)} className="px-6 py-2.5 bg-[#c8f542] text-black font-black uppercase tracking-widest rounded-xl shadow-lg hover:bg-[#d4ff50] transition-colors text-xs flex items-center space-x-2">
                      <SafeIcon icon={FiGrid} className="w-4 h-4" /><span>Create a Website</span>
                    </button>
                  </div>
                ) : activePageId ? (
                  <Canvas
                    key={`${activeWebsiteId}-${activePageId}`}
                    ref={canvasRef}
                    pageId={activePageId}
                    activeWebsiteId={activeWebsiteId}
                    activeWebsiteName={activeWebsiteName}
                    savedPageContent={pageContents[activePageId]}
                    menuStyles={menuStyles}
                    onMenuStylesChange={handleMenuStylesChange}
                    socialLinks={socialLinks}
                    onSocialLinksChange={setSocialLinks}
                    onHistoryChange={setHistoryState}
                    showPreview={showPreview}
                    onClosePreview={handleClosePreview}
                    onOpenPreview={handleOpenPreview}
                    activePage={activePage}
                    externalMenuItems={menuItems}
                    onMenuItemsChange={handleMenuItemsChange}
                    externalLogoData={logoData}
                    onLogoDataChange={handleLogoDataChange}
                    externalFooterData={footerData}
                    onFooterDataChange={handleFooterDataChange}
                    onPageStateChange={handleCanvasStateChange}
                    onSaveComplete={handleSaveComplete}
                    isReadOnly={false}
                    pages={pages}
                    onNavigateToPage={handleNavigateToPage}
                  />
                ) : (
                  // Canvas skeleton — shown only when no cache and fetching
                  <div className="flex-1 bg-[#f0f2f5] flex items-center justify-center flex-col space-y-4">
                    <div className="w-10 h-10 border-4 border-[#c8f542]/30 border-t-[#c8f542] rounded-full animate-spin" />
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">Loading canvas...</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <CartSidebar />
        <CheckoutModal />

        {/* Live modal */}
        <AnimatePresence>
          {showLiveModal && (
            <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
              <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden text-center p-8">
                <div className="w-20 h-20 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiCheckCircle} className="w-10 h-10" />
                </div>
                <h2 className="text-2xl font-black text-gray-900 mb-2">Your website is live!</h2>
                <p className="text-gray-500 mb-2 text-sm">Your changes have been published successfully.</p>
                {netlifySubdomain && (<p className="text-xs text-gray-400 mb-6 font-mono bg-gray-50 rounded-lg px-3 py-2 break-all">{netlifySubdomain}</p>)}
                {netlifyUrl && (
                  <a href={netlifyUrl.startsWith('http') ? netlifyUrl : `https://${netlifyUrl}`} target="_blank" rel="noopener noreferrer" onClick={() => setShowLiveModal(false)} className="w-full py-4 bg-[#c8f542] hover:bg-[#d4ff50] text-black font-black rounded-xl shadow-lg transition-all flex items-center justify-center space-x-2 mb-3">
                    <span>View Live Site</span><SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  </a>
                )}
                <button onClick={() => setShowLiveModal(false)} className="w-full py-3 font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-colors">Back to Builder</button>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Publish error modal */}
        <AnimatePresence>
          {publishError && (
            <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
              <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden text-center p-8 border border-red-100">
                <div className="w-20 h-20 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiAlertCircle} className="w-10 h-10" />
                </div>
                <h2 className="text-2xl font-black text-gray-900 mb-2">Publish Failed</h2>
                <p className="text-gray-500 text-sm mb-6">We couldn't deploy your website.</p>
                <div className="bg-red-50 rounded-xl p-4 mb-6 text-left border border-red-100">
                  <p className="text-red-800 text-sm font-medium leading-relaxed break-words">{publishError}</p>
                </div>
                <button onClick={() => setPublishError(null)} className="w-full py-4 bg-gray-900 hover:bg-black text-white font-black rounded-xl shadow-lg transition-all">Dismiss</button>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Super Admin Dashboard */}
        <AnimatePresence>
          {showSuperAdminDashboard && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.18 }} className="fixed inset-0 z-[999990] bg-white h-screen w-full flex flex-col">
              <SuperAdminDashboard
                onClose={() => { setShowSuperAdminDashboard(false); setView('builder'); }}
                onOpenWebsite={handleOpenWebsite}
                onOpenBuilder={() => { setShowSuperAdminDashboard(false); setView('builder'); }}
                onOpenStore={() => { setShowSuperAdminDashboard(false); setShowStoreDashboardPopup(true); }}
                onOpenSites={() => { setShowSuperAdminDashboard(false); setShowSitesPopup(true); }}
                menuStyles={menuStyles}
                onMenuStylesChange={handleMenuStylesChange}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Store dashboard */}
        <AnimatePresence>
          {showStoreDashboardPopup && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.18 }} className="fixed inset-0 z-[999990] bg-white h-screen w-full flex flex-col">
              <EcommerceDashboard
                onClose={() => { setShowStoreDashboardPopup(false); setView('builder'); }}
                menuStyles={menuStyles}
                onMenuStylesChange={handleMenuStylesChange}
                projectId={activeWebsiteId}
                activeWebsiteName={activeWebsiteName}
                onOpenSites={() => { setShowStoreDashboardPopup(false); setShowSitesPopup(true); }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sites dashboard */}
        <AnimatePresence>
          {showSitesPopup && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[999999] bg-[#0a0a0a] h-screen w-full flex flex-col">
              <SitesFunnelsDashboard
                onClose={() => { setShowSitesPopup(false); setView('builder'); }}
                onOpenWebsite={handleOpenWebsite}
                onOpenBuilder={() => { setShowSitesPopup(false); setView('builder'); }}
                onOpenStore={() => { setShowSitesPopup(false); setShowStoreDashboardPopup(true); }}
                menuStyles={menuStyles}
                onMenuStylesChange={handleMenuStylesChange}
                activeWebsiteName={activeWebsiteName}
                userId={user?.id}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </CartProvider>
  );
}

export default App;